import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Shield, Eye, Lock, Database, Mail, Globe, FileText, Users, Check } from 'lucide-react';
import { Link } from 'react-router-dom';

export const PrivacyPolicyPage = () => {
  const lastUpdated = 'January 2025';

  return (
    <div className="min-h-screen pt-16">
      {/* Hero Section */}
      <section className="py-24 bg-foreground relative overflow-hidden">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 mb-6">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-white mb-4">
            Privacy Policy
          </h1>
          <p className="text-xl text-white/70">
            How we collect, use, and protect your information
          </p>
          <p className="text-white/40 mt-6 text-sm">
            Last updated: {lastUpdated}
          </p>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-lg text-muted-foreground leading-relaxed">
            At 961Host, we are committed to protecting your privacy and ensuring the security of your personal information. 
            This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our 
            web hosting services.
          </p>
        </div>
      </section>

      {/* Sections */}
      <section className="py-16 bg-secondary/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          
          {/* Section 1 */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Eye className="w-5 h-5 text-primary" />
              </div>
              <h2 className="text-2xl font-semibold text-foreground">1. Information We Collect</h2>
            </div>
            <div className="space-y-6 pl-13">
              <div className="p-6 rounded-xl bg-white border border-border">
                <h3 className="font-semibold text-foreground mb-2">Account Information</h3>
                <p className="text-muted-foreground">Name, email address, billing address, and payment information when you sign up for our hosting services.</p>
              </div>
              <div className="p-6 rounded-xl bg-white border border-border">
                <h3 className="font-semibold text-foreground mb-2">Technical Data</h3>
                <p className="text-muted-foreground">IP addresses, browser type, device information, and access logs for security and service improvement purposes.</p>
              </div>
              <div className="p-6 rounded-xl bg-white border border-border">
                <h3 className="font-semibold text-foreground mb-2">Service Data</h3>
                <p className="text-muted-foreground">Information related to your hosting account, including domain names, website content stored on our servers, and resource usage statistics.</p>
              </div>
              <div className="p-6 rounded-xl bg-white border border-border">
                <h3 className="font-semibold text-foreground mb-2">Communication Data</h3>
                <p className="text-muted-foreground">Records of your communications with our support team, including support tickets and emails.</p>
              </div>
            </div>
          </div>

          {/* Section 2 */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Database className="w-5 h-5 text-primary" />
              </div>
              <h2 className="text-2xl font-semibold text-foreground">2. How We Use Your Information</h2>
            </div>
            <div className="p-6 rounded-xl bg-white border border-border">
              <ul className="space-y-4">
                {[
                  { title: 'Service Delivery', desc: 'To provide, maintain, and improve our web hosting services.' },
                  { title: 'Account Management', desc: 'To manage your account, process payments, and send service-related communications.' },
                  { title: 'Security', desc: 'To protect our services and users from fraud, abuse, and unauthorized access.' },
                  { title: 'Support', desc: 'To respond to your inquiries and provide customer support.' },
                  { title: 'Legal Compliance', desc: 'To comply with applicable laws and regulations.' },
                ].map((item, idx) => (
                  <li key={idx} className="flex gap-3">
                    <Check className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                    <div>
                      <span className="font-medium text-foreground">{item.title}:</span>
                      <span className="text-muted-foreground ml-1">{item.desc}</span>
                    </div>
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-sm text-muted-foreground border-t border-border pt-4">
                We do not sell your personal information to third parties.
              </p>
            </div>
          </div>

          {/* Section 3 */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Lock className="w-5 h-5 text-primary" />
              </div>
              <h2 className="text-2xl font-semibold text-foreground">3. Data Security</h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { title: 'Encryption', desc: 'All data transmissions are encrypted using SSL/TLS protocols.' },
                { title: 'Access Controls', desc: 'Strict access controls limit who can access your data.' },
                { title: 'Infrastructure Security', desc: 'Servers housed in secure data centers with physical security.' },
                { title: 'Regular Backups', desc: 'Regular backups to protect against data loss.' },
              ].map((item, idx) => (
                <div key={idx} className="p-5 rounded-xl bg-white border border-border">
                  <h3 className="font-semibold text-foreground mb-1">{item.title}</h3>
                  <p className="text-sm text-muted-foreground">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Section 4 */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Users className="w-5 h-5 text-primary" />
              </div>
              <h2 className="text-2xl font-semibold text-foreground">4. Data Sharing</h2>
            </div>
            <div className="p-6 rounded-xl bg-white border border-border">
              <p className="text-muted-foreground mb-4">We may share your information in the following circumstances:</p>
              <ul className="space-y-3">
                {[
                  { title: 'Service Providers', desc: 'With trusted third-party service providers who assist us (e.g., payment processors).' },
                  { title: 'Legal Requirements', desc: 'When required by law, court order, or government request.' },
                  { title: 'Business Protection', desc: 'To protect the rights, property, or safety of 961Host, our users, or the public.' },
                  { title: 'Business Transfers', desc: 'In connection with a merger, acquisition, or sale of assets.' },
                ].map((item, idx) => (
                  <li key={idx} className="flex gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2 shrink-0" />
                    <div>
                      <span className="font-medium text-foreground">{item.title}:</span>
                      <span className="text-muted-foreground ml-1">{item.desc}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Section 5 */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Globe className="w-5 h-5 text-primary" />
              </div>
              <h2 className="text-2xl font-semibold text-foreground">5. Cookies and Tracking</h2>
            </div>
            <div className="p-6 rounded-xl bg-white border border-border">
              <p className="text-muted-foreground mb-4">We use cookies and similar technologies for:</p>
              <div className="grid sm:grid-cols-3 gap-4">
                {[
                  { title: 'Essential', desc: 'Core website functionality' },
                  { title: 'Analytics', desc: 'Understanding visitor behavior' },
                  { title: 'Preferences', desc: 'Remembering your settings' },
                ].map((item, idx) => (
                  <div key={idx} className="p-4 rounded-lg bg-secondary/50 text-center">
                    <h4 className="font-medium text-foreground mb-1">{item.title}</h4>
                    <p className="text-sm text-muted-foreground">{item.desc}</p>
                  </div>
                ))}
              </div>
              <p className="text-sm text-muted-foreground mt-4">
                You can control cookie settings through your browser. Disabling cookies may affect some website functionality.
              </p>
            </div>
          </div>

          {/* Section 6 */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <FileText className="w-5 h-5 text-primary" />
              </div>
              <h2 className="text-2xl font-semibold text-foreground">6. Your Rights</h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { title: 'Access', desc: 'Request a copy of the personal data we hold about you.' },
                { title: 'Correction', desc: 'Request correction of inaccurate or incomplete data.' },
                { title: 'Deletion', desc: 'Request deletion of your personal data.' },
                { title: 'Data Portability', desc: 'Request a copy of your data in a portable format.' },
              ].map((item, idx) => (
                <div key={idx} className="p-5 rounded-xl bg-white border border-border">
                  <h3 className="font-semibold text-foreground mb-1">{item.title}</h3>
                  <p className="text-sm text-muted-foreground">{item.desc}</p>
                </div>
              ))}
            </div>
            <p className="text-sm text-muted-foreground mt-4">
              To exercise these rights, please <Link to="/contact" className="text-primary hover:underline">contact us</Link>.
            </p>
          </div>

        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Mail className="w-12 h-12 text-primary mx-auto mb-4" />
          <h2 className="text-2xl font-semibold text-foreground mb-3">
            Questions About Our Privacy Policy?
          </h2>
          <p className="text-muted-foreground mb-6">
            If you have any questions or concerns, please don't hesitate to contact us.
          </p>
          <Link 
            to="/contact" 
            className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-primary text-white hover:bg-primary/90 transition-colors font-medium"
          >
            Contact Us
          </Link>
        </div>
      </section>
    </div>
  );
};
